import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { SaveStatusIndicator } from './SaveStatusIndicator';
import type { SaveStack } from './util_saveStack';

interface TopWhiteBarEditControlsProps {
  saveStack: SaveStack;
  /** Left-aligned content (e.g. back link, title). */
  leftSlot?: ReactNode;
  /** Centre content (e.g. undo/redo — added in a later step). */
  middleSlot?: ReactNode;
  /** Right-aligned content, after the save-status indicator. */
  children?: ReactNode;
  className?: string;
}

/**
 * Sticky top bar for autosave edit pages. Adapted from the portal's
 * TopWhiteBarEditControls: same white-bar-with-save-status concept, with the
 * portal-specific props (edit-mode toggle, floating totals, permissions)
 * removed. History and undo/redo are passed in as slots instead.
 */
export function TopWhiteBarEditControls({
  saveStack,
  leftSlot,
  middleSlot,
  children,
  className,
}: TopWhiteBarEditControlsProps) {
  return (
    <div
      className={cn(
        'tw:sticky tw:top-0 tw:z-30 tw:-mx-8 tw:mb-6 tw:flex tw:h-12 tw:items-center tw:justify-between',
        'tw:border-b tw:border-ed-umber/10 tw:bg-white/90 tw:px-8 tw:backdrop-blur',
        'tw:shadow-[0_1px_8px_rgba(0,0,0,0.06)]',
        className,
      )}
    >
      <div className='tw:flex tw:items-center tw:gap-4'>{leftSlot}</div>
      <div className='tw:flex tw:items-center'>{middleSlot}</div>
      <div className='tw:flex tw:items-center tw:gap-3'>
        <SaveStatusIndicator saveStack={saveStack} />
        {children}
      </div>
    </div>
  );
}
